import { Injectable } from '@angular/core';

declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
  }
}

@Injectable({
  providedIn: 'root',
})
export class CrispService {
  constructor() {}

  /**
   * Identify a user in Crisp with email and nickname
   * @param email User's email address
   * @param nickname User's display name
   */
  identifyUser(email: string, nickname: string): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(['set', 'user:email', [email]]);
      window.$crisp.push(['set', 'user:nickname', [nickname]]);
    }
  }

  /**
   * Set company information for the user
   * @param companyData Object containing company information
   * - name: Company name (required)
   * - url: Company website URL (optional)
   * - description: Company description (optional)
   * - employment: Array of roles/positions (optional)
   * - geolocation: Array of [country, city] (optional)
   */
  setCompanyData(companyData: {
    name: string;
    url?: string;
    description?: string;
    employment?: string[];
    geolocation?: string[];
  }): void {
    if (typeof window !== 'undefined' && window.$crisp && companyData.name) {
      const companyPayload: any = {};

      if (companyData.url) {
        companyPayload.url = companyData.url;
      }

      if (companyData.description) {
        companyPayload.description = companyData.description;
      }

      if (companyData.employment && companyData.employment.length > 0) {
        companyPayload.employment = companyData.employment;
      }

      if (companyData.geolocation && companyData.geolocation.length > 0) {
        companyPayload.geolocation = companyData.geolocation;
      }

      // Push company data in Crisp's expected format: ["CompanyName", {optional fields}]
      window.$crisp.push(['set', 'user:company', [companyData.name, companyPayload]]);
    }
  }

  /**
   * Set custom session data (key-value pairs)
   * Keys must be lowercase with underscores or dashes
   * @param data Object containing session data
   */
  setSessionData(data: Record<string, any>): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      const sessionDataArray = Object.entries(data).map(([key, value]) => [
        key.toLowerCase().replace(/\s+/g, '_'),
        value,
      ]);
      window.$crisp.push(['set', 'session:data', [sessionDataArray]]);
    }
  }

  /**
   * Reset the Crisp session (clear user identification)
   * Call this on logout
   */
  resetSession(): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(['do', 'session:reset']);
    }
  }

  /**
   * Hide the Crisp chat widget
   */
  hideWidget(): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(['do', 'chat:hide']);
    }
  }

  /**
   * Show the Crisp chat widget
   */
  showWidget(): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(['do', 'chat:show']);
    }
  }

  /**
   * Open the Crisp chat widget
   */
  openChat(): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
    }
  }

  /**
   * Close the Crisp chat widget
   */
  closeChat(): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(['do', 'chat:close']);
    }
  }

  /**
   * Set user avatar URL
   * @param avatarUrl URL to the user's avatar image
   */
  setAvatar(avatarUrl: string): void {
    if (typeof window !== 'undefined' && window.$crisp && avatarUrl) {
      window.$crisp.push(['set', 'user:avatar', [avatarUrl]]);
    }
  }

  /**
   * Set the Crisp widget position based on text direction.
   * In RTL mode (Arabic) the widget is moved to the bottom-left so it does
   * not overlap the right-side navigation icons.
   * @param isRTL pass true when the active language is RTL (e.g. Arabic)
   */
  setPosition(isRTL: boolean): void {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(['config', 'position:reverse', [isRTL]]);
    }
  }
}
